import { z } from 'zod';
/**
 * Affiliates (`product/business-rules.md`, Affiliates): every account has a
 * referral link. An account that signs up through it, and claims it within
 * `REFERRAL_CLAIM_DAYS` of being created, is the affiliate's referral: the
 * affiliate earns `AFFILIATE_COMMISSION_PERCENT` of every invoice it pays
 * for its first `AFFILIATE_COMMISSION_MONTHS` months, and it gets
 * `REFERRAL_DISCOUNT_PERCENT` off list price for `REFERRAL_DISCOUNT_CYCLES`
 * cycles. Groundly pays commissions out by hand and records each payout.
 */
export const AFFILIATE_COMMISSION_PERCENT = 20;
export const AFFILIATE_COMMISSION_MONTHS = 12;
export const REFERRAL_DISCOUNT_PERCENT = 30;
export const REFERRAL_DISCOUNT_CYCLES = 3;
export const REFERRAL_CLAIM_DAYS = 30;
export const MAX_PAYOUT_DETAILS_LENGTH = 500;
/** A referral code: 8 letters and digits, entered in any case. */
export const referralCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{6,12}$/, 'Enter the referral code exactly as you were given it.');
const cents = z.number().int();
export const affiliateTotalsSchema = z.object({
    /** Accounts referred. */
    referred: z.number().int().min(0),
    /** Of those, the ones that have paid an invoice. */
    paying: z.number().int().min(0),
    earnedCents: cents,
    paidOutCents: cents,
    /** Earned less paid out; negative when a paid invoice was later marked unpaid. */
    balanceCents: cents,
});
export const affiliateCommissionSchema = z.object({
    invoiceNumber: z.number().int(),
    /** Which referral it came from, as numbered on the page: never the account itself. */
    referralLabel: z.string(),
    periodStart: z.string(),
    periodEnd: z.string(),
    invoiceTotalCents: cents,
    commissionCents: cents,
    paidAt: z.iso.datetime(),
});
export const affiliatePayoutSchema = z.object({
    id: z.uuid(),
    amountCents: cents,
    reference: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const affiliateReferralSchema = z.object({
    id: z.uuid(),
    /** "Referral 1", "Referral 2": in the order they joined. */
    label: z.string(),
    referredAt: z.iso.datetime(),
    /** The last cycle start that still earns commission. */
    commissionUntil: z.string(),
    paidInvoices: z.number().int().min(0),
    earnedCents: cents,
});
export const affiliateResponseSchema = z.object({
    code: z.string(),
    /** The sign-up link with the code, to share. */
    link: z.string(),
    commissionPercent: z.number().int(),
    commissionMonths: z.number().int(),
    referralDiscountPercent: z.number().int(),
    referralDiscountCycles: z.number().int(),
    /** Where Groundly should send payouts: PayPal, Wise or bank details, as the account wrote them. */
    payoutDetails: z.string().nullable(),
    totals: affiliateTotalsSchema,
    referrals: z.array(affiliateReferralSchema),
    /** Newest first. */
    commissions: z.array(affiliateCommissionSchema),
    /** Newest first. */
    payouts: z.array(affiliatePayoutSchema),
    /** This account's own referral: the code it came with, or whether it can still enter one. */
    referredBy: z.object({ code: z.string().nullable(), canClaim: z.boolean() }),
});
// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
/** The account's affiliate page. Needs the billing and invoices permission. */
export const getAffiliate = {
    method: 'GET',
    path: '/api/dashboard/affiliate',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: affiliateResponseSchema,
};
export const updatePayoutDetailsBodySchema = z.object({
    payoutDetails: z
        .string()
        .trim()
        .max(MAX_PAYOUT_DETAILS_LENGTH, `Keep it under ${MAX_PAYOUT_DETAILS_LENGTH} characters.`),
});
/** Needs the billing and invoices permission. */
export const updatePayoutDetails = {
    method: 'PATCH',
    path: '/api/dashboard/affiliate/payout-details',
    params: z.object({}),
    query: z.object({}),
    body: updatePayoutDetailsBodySchema,
    response: affiliateResponseSchema,
};
export const claimReferralBodySchema = z.object({ code: referralCodeSchema });
/**
 * Any Account User: the dashboard sends the code a sign-up link carried right
 * after the new account's first log-in, or someone enters it on the
 * affiliate page. `CONFLICT` once the account has a referral, is older than
 * `REFERRAL_CLAIM_DAYS`, has paid an invoice, or names its own code;
 * `NOT_FOUND` for an unknown code.
 */
export const claimReferral = {
    method: 'POST',
    path: '/api/dashboard/affiliate/referral',
    params: z.object({}),
    query: z.object({}),
    body: claimReferralBodySchema,
    response: z.object({ discountPercent: z.number().int(), discountCycles: z.number().int() }),
};
// ---------------------------------------------------------------------------
// Admin panel
// ---------------------------------------------------------------------------
export const adminAffiliateRowSchema = affiliateTotalsSchema.extend({
    accountId: z.uuid(),
    ownerEmail: z.string().nullable(),
    code: z.string(),
    payoutDetails: z.string().nullable(),
});
/** Affiliates with at least one referral, the largest balance first. */
export const listAdminAffiliates = {
    method: 'GET',
    path: '/api/admin/affiliates',
    params: z.object({}),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ items: z.array(adminAffiliateRowSchema) }),
};
export const adminAffiliateSchema = adminAffiliateRowSchema.extend({
    referrals: z.array(affiliateReferralSchema.extend({
        accountId: z.uuid(),
        ownerEmail: z.string().nullable(),
    })),
    commissions: z.array(affiliateCommissionSchema),
    payouts: z.array(affiliatePayoutSchema.extend({ recordedBy: z.string() })),
});
export const getAdminAffiliate = {
    method: 'GET',
    path: '/api/admin/affiliates/:accountId',
    params: z.object({ accountId: z.uuid() }),
    query: z.object({}),
    body: z.object({}),
    response: adminAffiliateSchema,
};
export const recordAffiliatePayoutBodySchema = z.object({
    amountCents: z.number().int().min(1),
    reference: z.string().trim().max(200).optional(),
});
/** Records money Groundly sent the affiliate; it comes off their balance. Audited. */
export const recordAffiliatePayout = {
    method: 'POST',
    path: '/api/admin/affiliates/:accountId/payouts',
    params: z.object({ accountId: z.uuid() }),
    query: z.object({}),
    body: recordAffiliatePayoutBodySchema,
    response: adminAffiliateSchema,
};
//# sourceMappingURL=affiliate.js.map